// Landing page targeting parents shopping for their college kids:
// "gifts for college students", "college send-off gift", "what to get my
// son/daughter for college". Buyer is the parent; the student page at
// /wallet-for-college-students targets the student directly.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";
import idPhoto from "../assets/newest-version/ID Card Holder.jpg";
import keysPhoto from "../assets/newest-version/Back - Keys Focus.jpg";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";

const futura = "'Futura', sans-serif";

const PARENT_FAQ = [
  {
    q: "Why is The Brik a good gift for a college student?",
    a: "Because they'll use it every day. The Brik holds the student ID they swipe into the dorm and dining hall, the keys they can't afford to lose, plus cards and cash, all in one metal wallet with a tracking card built in. It replaces the lanyard, the phone-case ID, and the loose keys in one gift.",
  },
  {
    q: "What happens when they lose it?",
    a: "They find it. Every Brik ships with a rechargeable tracking card, so they can see the wallet's location on a map or make it ring from their phone. The battery lasts up to 6 months per charge and recharges on any wireless charger, with no subscription.",
  },
  {
    q: "Will it survive dorm life?",
    a: "The body is machined from solid anodized aluminum and is built for being dropped in backpacks, pockets, and dorm floors. If it ever structurally fails during normal use, we replace it under warranty.",
  },
  {
    q: "Does it work with their student ID and dorm keys?",
    a: "Yes. The ID sits in a quick-access front slot that stays scannable for tap-based campus IDs, and the included removable keyring holds dorm keys, mailbox keys, and car keys. The main compartment holds 7-8 cards with RFID protection.",
  },
  {
    q: "Do I need to know if they use an iPhone or Android?",
    a: "Yes, that's the only thing you need to know. Pick Apple or Android at checkout so the Brik ships with the right tracking card for their phone.",
  },
  {
    q: "How fast does it ship?",
    a: "Orders are processed in about 1 business day and arrive within 5-7 days. For a graduation or move-in deadline, we recommend ordering at least two weeks ahead. There's a 30-day exchange guarantee if anything arrives wrong.",
  },
];

const OCCASIONS = [
  {
    title: "High school graduation gift",
    text: "A grown-up upgrade before they leave: their new student ID goes in the front, their first dorm key goes on the ring, and they head to campus organized on day one.",
  },
  {
    title: "College send-off and move-in day",
    text: "Move-in is when the lanyard-and-loose-keys chaos starts. Handing them a Brik at drop-off means everything that opens a door lives in one place from the first night.",
  },
  {
    title: "Care package upgrade",
    text: "Snacks are gone in a week. A Brik shows up mid-semester, right around when the first 'I can't find my ID' text usually does.",
  },
  {
    title: "Birthdays and holidays away at school",
    text: "A $69.99 gift that ships to campus in 5-7 days and gets carried every single day. It beats another hoodie.",
  },
];

// Real metal-Brik buyer reviews (full set of 20 on /metal-wallet).
const STUDENT_REVIEWS = [
  {
    text: "I bought it as a gift for my son in college, and he uses it every day.",
    label: "Verified buyer",
  },
  {
    text: "I bought it because I constantly lose my keys, and having everything attached has actually helped.",
    label: "Verified buyer",
  },
  {
    text: "It sounds simple, but knowing my wallet and keys are always together makes leaving the house easier.",
    label: "Verified buyer",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const CollegeParentGifts = () => {
  const faqJsonLd = makeFaqJsonLd(PARENT_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Gifts for College Students", path: "/gifts-for-college-students" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Gifts for College Students Who Lose Everything"
        description="Looking for a gift your college kid will actually use? The Brik keeps their student ID, dorm keys, cards, and cash in one trackable metal wallet. $69.99, ships in 5-7 days."
        path="/gifts-for-college-students"
        keywords="gifts for college students, college send-off gift, gift for son going to college, gift for daughter going to college, high school graduation gift, college care package ideas, practical gifts for college kids, dorm essentials"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            FOR COLLEGE PARENTS
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            Gifts for College Students Who Lose Everything
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            The best gift for a college student is one they use every day. The
            Brik keeps their student ID, dorm keys, cards, and cash in one
            metal wallet with a tracking card built in, so when it goes
            missing on campus, they find it from their phone. $69.99.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            In stock · arrives in 5-7 days · 30-day exchange guarantee
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Gift for a college student: The Brik metal wallet holding student ID, cards, cash, and dorm keys"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* The parent math */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>You already know the phone call</h2>
          <p style={bodyText}>
            "I can't find my ID." "I think I left my keys somewhere." Lost
            student IDs mean replacement fees and being locked out of the
            dining hall. A lost dorm key can mean a lock change that runs well
            over a hundred dollars. Our founder started The Brik at Iowa State
            after one lost dorm key cost him $160.
          </p>
          <p style={bodyText}>
            The problem isn't that your kid is careless. It's that campus life
            hands them three separate things to lose: an ID they swipe ten
            times a day, keys on a bare lanyard, and a wallet buried in a
            backpack. The Brik makes them one thing, and makes that one thing
            findable from their phone.
          </p>
          <p style={{ ...bodyText, margin: 0 }}>
            One $69.99 gift versus the first lock-change fee. That's the math.
          </p>
        </div>
      </section>

      {/* What it replaces / features */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "40px" }}>
            One gift, three problems solved
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "28px",
            }}
          >
            {[
              {
                img: idPhoto,
                alt: "Student ID in the front slot of the Brik metal wallet, a practical gift for college students",
                title: "Replaces the lanyard",
                text: "Their student ID rides in the front slot, facing out and scannable. Swiping into the dorm, dining hall, gym, and library is one motion.",
              },
              {
                img: keysPhoto,
                alt: "Dorm keys attached to the Brik wallet keyring, a useful college gift from parents",
                title: "Replaces the loose keys",
                text: "Dorm key, mailbox key, and car key attach to the included keyring. Wallet and keys become one thing, so losing track of one no longer means losing both.",
              },
              {
                img: trackerPhoto,
                alt: "Rechargeable tracking card inside the Brik wallet giving parents peace of mind",
                title: "Replaces the panic",
                text: "The built-in tracking card means a misplaced wallet is a two-minute find, not a campus-wide search. Ring it, see it on a map, move on with the day.",
              },
            ].map((f) => (
              <div key={f.title}>
                <div
                  style={{
                    background: "#989898",
                    borderRadius: "14px",
                    overflow: "hidden",
                    aspectRatio: "4 / 3",
                    marginBottom: "16px",
                  }}
                >
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    width={1024}
                    height={1024}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "19px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "15px", margin: 0 }}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            The right moment to give it
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "20px",
            }}
          >
            {OCCASIONS.map((o) => (
              <div
                key={o.title}
                style={{
                  background: "#f7f7f7",
                  borderRadius: "14px",
                  padding: "26px 24px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "17px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {o.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "15px", margin: 0 }}>{o.text}</p>
              </div>
            ))}
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "28px 0 0",
            }}
          >
            Shopping for a wedding party instead? See the{" "}
            <a href="/groomsmen-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              groomsmen gift packs
            </a>
            . Want the student's-eye view? Read why it's{" "}
            <a
              href="/wallet-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              the wallet built for college students
            </a>
            .
          </p>
        </div>
      </section>

      {/* Student reviews */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            What students say
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {STUDENT_REVIEWS.map((r) => (
              <div
                key={r.name}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  flex: "1 1 260px",
                  maxWidth: "320px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <div
                  style={{ color: "#f5b315", fontSize: "16px", letterSpacing: "2px", marginBottom: "10px" }}
                  aria-label="5 out of 5 stars"
                >
                  ★★★★★
                </div>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.55,
                    color: "#222",
                    fontStyle: "italic",
                    margin: "0 0 14px",
                  }}
                >
                  &ldquo;{r.text}&rdquo;
                </p>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#111",
                    margin: 0,
                  }}
                >
                  {r.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Parent questions, answered
          </h2>
          {PARENT_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            Send them back organized.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            $69.99, in stock, at their dorm in 5-7 days. Just pick Apple or
            Android at checkout so it ships with the right tracking card for
            their phone.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CollegeParentGifts;
